import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Settings,
  Image,
  Palette,
  Globe,
  Save,
  RefreshCw,
  Store,
  Smartphone,
  Check,
  Building2,
  CreditCard,
  Crown
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SalesCompanySettingsPage = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [salesCompanies, setSalesCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [savingPlan, setSavingPlan] = useState(false);
  
  const isPlatformAdmin = user?.tenant_role === 'platform_admin' || 
                          user?.role_id === 'role_platform_admin' ||
                          user?.role_id === 'role_developer';
  
  const isBuilderAdmin = user?.tenant_role === 'builder_admin';
  
  const [settings, setSettings] = useState({
    company_id: '',
    company_name: '',
    builder_id: '',
    subscription_plan_id: '',
    subscription_status: 'none',
    branding: {
      logo_url: '',
      logo_link_url: '',
      favicon_url: '',
      pwa_icon_url: '',
      primary_color: '',
      secondary_color: '',
      page_title: '',
      page_description: ''
    },
    has_custom_branding: false
  });
  
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchSubscriptionPlans();
    if (isPlatformAdmin || isBuilderAdmin) {
      fetchSalesCompanies();
    } else if (user?.sales_company_id) {
      setSelectedCompanyId(user.sales_company_id);
      fetchSettings(user.sales_company_id);
    }
  }, [user]);

  useEffect(() => {
    if (selectedCompanyId) {
      fetchSettings(selectedCompanyId);
    }
  }, [selectedCompanyId]);

  const fetchSubscriptionPlans = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/billing/plans`, { headers });
      setSubscriptionPlans(response.data.filter(p => !p.is_hidden));
    } catch (error) {
      console.error('Failed to fetch subscription plans:', error);
    }
  };

  const fetchSalesCompanies = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/sales-companies/`, { headers });
      setSalesCompanies(response.data);
      if (response.data.length > 0 && !selectedCompanyId) {
        setSelectedCompanyId(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch sales companies:', error);
    }
  };

  const fetchSettings = async (companyId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/settings/sales-company/${companyId}`, { headers });
      setSettings({
        ...response.data,
        custom_domain: response.data.custom_domain || '',
        subdomain: response.data.subdomain || '',
        subscription_plan_id: response.data.subscription_plan_id || '',
        subscription_status: response.data.subscription_status || 'none'
      });
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load sales company settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    if (!selectedCompanyId) return;
    
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/settings/sales-company/${selectedCompanyId}/full`,
        {
          branding: settings.branding,
          custom_domain: settings.custom_domain || null
        },
        { headers }
      );
      toast.success('Sales company settings saved successfully!');
      fetchSettings(selectedCompanyId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const saveSubscriptionPlan = async (planId) => {
    if (!selectedCompanyId) return;
    
    setSavingPlan(true);
    try {
      await axios.put(
        `${API_URL}/api/sales-companies/${selectedCompanyId}/subscription`,
        { plan_id: planId },
        { headers }
      );
      toast.success('Subscription plan updated successfully!');
      setSettings(prev => ({ ...prev, subscription_plan_id: planId, subscription_status: 'active' }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update subscription plan');
    } finally {
      setSavingPlan(false);
    }
  };

  const updateBranding = (field, value) => {
    setSettings({
      ...settings,
      branding: {
        ...settings.branding,
        [field]: value
      }
    });
  };

  const clearField = (field) => {
    updateBranding(field, '');
  };

  if (loading && !selectedCompanyId) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="sales-company-settings-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
              <Store className="w-7 h-7 text-green-600" />
              Sales Company Branding
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              Customize branding for your sales portal
            </p>
          </div>
          <Button onClick={saveSettings} disabled={saving || !selectedCompanyId} data-testid="save-company-settings">
            {saving ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>

        {/* Company Selector (Platform/Builder Admin Only) */}
        {(isPlatformAdmin || isBuilderAdmin) && salesCompanies.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <Label className="text-gray-700 dark:text-gray-200 whitespace-nowrap">Select Company:</Label>
                <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
                  <SelectTrigger className="w-72 bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
                    <SelectValue placeholder="Select a sales company..." />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-gray-800">
                    {salesCompanies.map(company => (
                      <SelectItem key={company.id} value={company.id} className="text-gray-900 dark:text-white">
                        <div className="flex items-center gap-2">
                          <Store className="w-4 h-4" />
                          {company.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {settings.has_custom_branding && (
                  <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                    <Check className="w-3 h-3 mr-1" />
                    Custom Branding Active
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Subscription Plan Section */}
        {selectedCompanyId && isPlatformAdmin && (
          <Card className="border-2 border-amber-200 dark:border-amber-800 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
                <Crown className="w-5 h-5" />
                Subscription Plan
              </CardTitle>
              <CardDescription className="text-amber-700 dark:text-amber-300">
                Manage this company's subscription and billing
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Label className="text-amber-800 dark:text-amber-200">Current Plan</Label>
                  <Select 
                    value={settings.subscription_plan_id || 'none'} 
                    onValueChange={(value) => {
                      if (value !== 'none') {
                        saveSubscriptionPlan(value);
                      }
                    }}
                    disabled={savingPlan}
                  >
                    <SelectTrigger className="w-full mt-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
                      <SelectValue placeholder="Select a plan..." />
                    </SelectTrigger>
                    <SelectContent className="bg-white dark:bg-gray-800">
                      <SelectItem value="none" className="text-gray-900 dark:text-white">
                        <div className="flex items-center gap-2">
                          <CreditCard className="w-4 h-4 text-gray-400" />
                          No Plan (Free Tier)
                        </div>
                      </SelectItem>
                      {subscriptionPlans.map(plan => (
                        <SelectItem key={plan.id} value={plan.id} className="text-gray-900 dark:text-white">
                          <div className="flex items-center gap-2">
                            <Crown className="w-4 h-4 text-amber-500" />
                            {plan.name} - ${plan.price_monthly}/mo
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-right">
                  <Label className="text-amber-800 dark:text-amber-200">Status</Label>
                  <div className="mt-1">
                    {settings.subscription_status === 'active' ? (
                      <Badge className="bg-green-500 text-white">Active</Badge>
                    ) : settings.subscription_status === 'trial' ? (
                      <Badge className="bg-blue-500 text-white">Trial</Badge>
                    ) : settings.subscription_status === 'past_due' ? (
                      <Badge className="bg-red-500 text-white">Past Due</Badge>
                    ) : (
                      <Badge className="bg-gray-400 text-white">No Plan</Badge>
                    )}
                  </div>
                </div>
              </div>
              
              {settings.subscription_plan_id && (
                <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-amber-200 dark:border-amber-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {subscriptionPlans.find(p => p.id === settings.subscription_plan_id)?.name || 'Unknown Plan'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {subscriptionPlans.find(p => p.id === settings.subscription_plan_id)?.features?.slice(0, 2).join(', ')}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-amber-600 dark:text-amber-400">
                        ${subscriptionPlans.find(p => p.id === settings.subscription_plan_id)?.price_monthly || 0}
                        <span className="text-xs font-normal">/mo</span>
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {savingPlan && (
                <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Updating subscription...</span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Logo Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Image className="w-5 h-5 text-green-600" />
                  Logo Settings
                </CardTitle>
                <CardDescription>
                  Custom logos for {settings.company_name || 'your company'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Logo URL */}
                <div>
                  <Label className="text-gray-700 dark:text-gray-200">Logo URL</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      placeholder="https://example.com/logo.png"
                      value={settings.branding.logo_url || ''}
                      onChange={(e) => updateBranding('logo_url', e.target.value)}
                      className="flex-1"
                    />
                    {settings.branding.logo_url && (
                      <Button variant="outline" size="sm" onClick={() => clearField('logo_url')}>
                        Clear
                      </Button>
                    )}
                  </div>
                  {settings.branding.logo_url && (
                    <div className="mt-2 p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
                      <img 
                        src={settings.branding.logo_url} 
                        alt="Logo Preview" 
                        className="max-h-12 object-contain"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}
                </div>

                {/* Logo Link URL */}
                <div>
                  <Label className="text-gray-700 dark:text-gray-200">Logo Link URL</Label>
                  <Input
                    placeholder="/"
                    value={settings.branding.logo_link_url || ''}
                    onChange={(e) => updateBranding('logo_link_url', e.target.value)}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Where the logo links when clicked</p>
                </div>

                {/* Favicon URL */}
                <div>
                  <Label className="text-gray-700 dark:text-gray-200">Favicon URL</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      placeholder="https://example.com/favicon.ico"
                      value={settings.branding.favicon_url || ''}
                      onChange={(e) => updateBranding('favicon_url', e.target.value)}
                      className="flex-1"
                    />
                    {settings.branding.favicon_url && (
                      <img 
                        src={settings.branding.favicon_url} 
                        alt="Favicon" 
                        className="w-8 h-8 object-contain"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    )}
                  </div>
                </div>

                {/* PWA Icon URL */}
                <div>
                  <Label className="text-gray-700 dark:text-gray-200">PWA Icon URL</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      placeholder="https://example.com/icon-512.png"
                      value={settings.branding.pwa_icon_url || ''}
                      onChange={(e) => updateBranding('pwa_icon_url', e.target.value)}
                      className="flex-1"
                    />
                    {settings.branding.pwa_icon_url && (
                      <img 
                        src={settings.branding.pwa_icon_url} 
                        alt="PWA Icon" 
                        className="w-10 h-10 object-contain rounded-lg"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">512x512 PNG recommended</p>
                </div>
              </CardContent>
            </Card>

            {/* Color & SEO Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Palette className="w-5 h-5 text-green-600" />
                  Colors & SEO
                </CardTitle>
                <CardDescription>
                  Brand colors and page metadata for your portal
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Primary Color */}
                <div>
                  <Label className="text-gray-700 dark:text-gray-200">Primary Color</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="color"
                      value={settings.branding.primary_color || '#2563EB'}
                      onChange={(e) => updateBranding('primary_color', e.target.value)}
                      className="w-14 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={settings.branding.primary_color || ''}
                      onChange={(e) => updateBranding('primary_color', e.target.value)}
                      placeholder="Leave empty for inherited default"
                      className="flex-1"
                    />
                  </div>
                </div>

                {/* Secondary Color */}
                <div>
                  <Label className="text-gray-700 dark:text-gray-200">Secondary Color</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="color"
                      value={settings.branding.secondary_color || '#F97316'}
                      onChange={(e) => updateBranding('secondary_color', e.target.value)}
                      className="w-14 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={settings.branding.secondary_color || ''}
                      onChange={(e) => updateBranding('secondary_color', e.target.value)}
                      placeholder="Leave empty for inherited default"
                      className="flex-1"
                    />
                  </div>
                </div>

                {/* Color Preview */}
                <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
                  <p className="text-xs text-gray-500 mb-2">Preview</p>
                  <div className="flex gap-3">
                    <button 
                      className="px-3 py-1.5 rounded text-white text-sm"
                      style={{ backgroundColor: settings.branding.primary_color || '#2563EB' }}
                    >
                      Primary
                    </button>
                    <button 
                      className="px-3 py-1.5 rounded text-white text-sm"
                      style={{ backgroundColor: settings.branding.secondary_color || '#F97316' }}
                    >
                      Secondary
                    </button>
                  </div>
                </div>

                {/* Page Title */}
                <div>
                  <Label className="text-gray-700 dark:text-gray-200">Page Title</Label>
                  <Input
                    placeholder={settings.company_name || 'Company name'}
                    value={settings.branding.page_title || ''}
                    onChange={(e) => updateBranding('page_title', e.target.value)}
                    className="mt-1"
                  />
                </div>

                {/* Page Description */}
                <div>
                  <Label className="text-gray-700 dark:text-gray-200">Page Description</Label>
                  <textarea
                    className="w-full mt-1 px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white dark:border-gray-600"
                    rows={3}
                    placeholder="Description for search engines..."
                    value={settings.branding.page_description || ''}
                    onChange={(e) => updateBranding('page_description', e.target.value)}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Custom Domain Configuration */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  Custom Domain
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-gray-700 dark:text-gray-200">Custom Domain</Label>
                  <Input
                    placeholder="builder.kayjaxsteel.com"
                    value={settings.custom_domain || ''}
                    onChange={(e) => setSettings({ ...settings, custom_domain: e.target.value })}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Your own domain for the branded configurator (requires DNS setup)
                  </p>
                </div>
                
                {settings.custom_domain && (
                  <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                    <h5 className="font-medium text-amber-800 dark:text-amber-200 text-sm mb-2">⚠️ Custom Domain Note</h5>
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                      Custom domains require server wildcard SSL support. Use the <strong>Branded URL</strong> below instead - it works immediately!
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Configurator URLs */}
        {selectedCompanyId && (
          <Card className="bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Globe className="w-5 h-5 text-purple-600 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-medium text-purple-900 dark:text-purple-200 mb-3">Configurator URLs</h4>
                  
                  {/* Branded URL - Primary */}
                  <div className="mb-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-700">
                    <p className="text-sm text-green-700 dark:text-green-300 mb-1 flex items-center gap-2">
                      <span className="inline-block w-2 h-2 bg-green-500 rounded-full"></span>
                      <strong>Branded URL</strong> (Recommended - Works Now!):
                    </p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 px-3 py-2 bg-white dark:bg-gray-800 rounded border border-green-300 dark:border-green-700 text-sm text-gray-900 dark:text-white font-mono truncate">
                        {window.location.origin}/c/{settings.company_name?.toLowerCase().replace(/\s+/g, '-') || 'your-company'}
                      </code>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-green-500 text-green-700 hover:bg-green-50"
                        onClick={() => {
                          const slug = settings.company_name?.toLowerCase().replace(/\s+/g, '-') || 'your-company';
                          const url = `${window.location.origin}/c/${slug}`;
                          navigator.clipboard.writeText(url);
                          toast.success('URL copied to clipboard!');
                        }}
                      >
                        Copy
                      </Button>
                    </div>
                    <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                      Share this URL with your customers. Your branding will be displayed automatically.
                    </p>
                  </div>
                  
                  {/* Alternative URL with query param */}
                  <div className="mb-3">
                    <p className="text-sm text-purple-700 dark:text-purple-300 mb-1">
                      <strong>Alternative URL</strong> (also works):
                    </p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 px-3 py-2 bg-white dark:bg-gray-800 rounded border border-purple-300 dark:border-purple-700 text-sm text-gray-900 dark:text-white font-mono truncate">
                        {window.location.origin}/demo?tenant={settings.company_name?.toLowerCase().replace(/\s+/g, '-') || 'your-company'}
                      </code>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const url = `${window.location.origin}/demo?tenant=${settings.company_name?.toLowerCase().replace(/\s+/g, '-') || 'your-company'}`;
                          navigator.clipboard.writeText(url);
                          toast.success('URL copied to clipboard!');
                        }}
                      >
                        Copy
                      </Button>
                    </div>
                  </div>
                  
                  {/* Staff Login URL */}
                  <div className="mt-4 pt-4 border-t border-purple-200 dark:border-purple-700">
                    <p className="text-sm text-purple-700 dark:text-purple-300 mb-1">
                      <strong>Staff Login URL</strong> (for your sales team):
                    </p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 px-3 py-2 bg-white dark:bg-gray-800 rounded border border-purple-300 dark:border-purple-700 text-sm text-gray-900 dark:text-white font-mono truncate">
                        {window.location.origin}/c/{settings.company_name?.toLowerCase().replace(/\s+/g, '-') || 'your-company'}/login
                      </code>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const slug = settings.company_name?.toLowerCase().replace(/\s+/g, '-') || 'your-company';
                          const url = `${window.location.origin}/c/${slug}/login`;
                          navigator.clipboard.writeText(url);
                          toast.success('Login URL copied to clipboard!');
                        }}
                      >
                        Copy
                      </Button>
                    </div>
                    <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                      Branded login page for your sales staff to access the CRM
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Globe className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-green-900 dark:text-green-200">Inheritance Chain</h4>
                  <p className="text-sm text-green-700 dark:text-green-300">
                    Settings inherit: Platform → Builder → Sales Company. 
                    Empty fields use parent values.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Smartphone className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-900 dark:text-blue-200">White-Label Ready</h4>
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    Your customers will see your branding when accessing their portal via subdomain.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SalesCompanySettingsPage;
